import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { SxProps } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link, LinkProps } from 'react-router-dom';

import { Box } from './Box';
import { Button, ButtonProps } from './Button';
import { Icon } from './Icon';

export interface ButtonPropsWithLabel extends ButtonProps {
  label: string;
  icon?: any;
}

interface PageHeaderProps {
  title: React.ReactNode;
  titleVariant?: 'h4' | 'h5' | 'h6';
  actions?: React.ReactNode | ButtonPropsWithLabel[];
  row?: React.ReactNode;
  backButtonTitle?: string;
  backButtonLinkTo?: LinkProps['to'] | (() => void);
  sx?: SxProps;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  titleVariant,
  actions,
  row,
  backButtonTitle,
  backButtonLinkTo,
  sx,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'pageHeader' });
  const showBackButton = backButtonLinkTo && backButtonTitle;
  const isBackFunction = typeof backButtonLinkTo === 'function';
  return (
    <Box pt={showBackButton ? 4 : 7} mb={4} sx={sx}>
      {showBackButton && (
        <Button
          component={isBackFunction ? 'button' : Link}
          to={isBackFunction ? undefined : backButtonLinkTo}
          color="info"
          startIcon={
            <Icon icon={icon({ name: 'arrow-left', style: 'regular' })} />
          }
          sx={{ mb: 2 }}
          onClick={isBackFunction ? backButtonLinkTo : undefined}
        >
          {t('backTo', { title: backButtonTitle })}
        </Button>
      )}
      <Stack
        direction="row"
        spacing={4}
        sx={{ mb: 3, justifyContent: 'space-between' }}
      >
        <Box>
          <Typography
            sx={{ lineHeight: 'inherit' }}
            variant={titleVariant || 'h3'}
          >
            {title}
          </Typography>
        </Box>
        {!!actions && (
          <Stack
            spacing={2}
            direction="row"
            sx={{
              alignItems: 'center',
            }}
          >
            {Array.isArray(actions) &&
              actions.map((buttonProps, i) => (
                <Button
                  key={i}
                  startIcon={<Icon icon={buttonProps.icon} />}
                  {...buttonProps}
                >
                  {buttonProps.children || buttonProps.label}
                </Button>
              ))}
            {!Array.isArray(actions) && actions}
          </Stack>
        )}
      </Stack>
      {row && (
        <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
          {row}
        </Stack>
      )}
    </Box>
  );
};
